// Directory manifest verification — the most important few dozen lines in this app.
//
// The manifest tells the client where companies' servers are. Anyone able to plant
// their own manifest could point any number at any server, which is why the client
// checks THREE conditions at once and rejects the data if any of them fails:
//
// 1. A signature by a k-of-n threshold of allowed keys — blocks forging an entry.
// 2. A version no older than the last accepted one — blocks a rollback to old but
//    correctly signed content that would bring back a withdrawn server.
// 3. A validity window — blocks freezing in time: serving the last genuine content
//    over and over so the client never learns of any change.
//
// With those three, a hijacked directory domain can only deny service. It cannot lie.

import { canonical } from './canonical-json';
import { utf8 } from './hash';
import { countValidSignatures } from './threshold';

export type ManifestRejection =
  | 'unreadable_response'
  | 'manifest_bad_signature'
  | 'unsupported_version'
  | 'manifest_too_old'
  | 'manifest_expired'
  | 'clock_unreliable';

export type ManifestCheck =
  | { accepted: true; body: Record<string, unknown> }
  | { accepted: false; error: ManifestRejection };

export interface ManifestVerifierOptions {
  /**
   * Public keys allowed to sign, base64url without padding, indexed by fingerprint.
   * On first launch they come from the compiled-in list, afterwards from the previous
   * manifest together with its `keys.future` set — which is what lets a key be
   * rotated without shipping a new build.
   */
  allowedKeys: ReadonlyMap<string, string>;
  /** How many signatures have to check out (k of n). */
  threshold: number;
  /** Fallback when the manifest carries no `clock_tolerance_s`. */
  clockToleranceS?: number;
  expectedProtocolVersion?: number;
}

/**
 * [knownVersion] is the highest version this client has ever accepted. [now] is
 * injected so tests do not depend on the machine clock.
 */
export async function verifyManifest(
  manifest: unknown,
  options: ManifestVerifierOptions & { knownVersion: number; now: Date },
): Promise<ManifestCheck> {
  const clockToleranceS = options.clockToleranceS ?? 7200;
  const expectedProtocol = options.expectedProtocolVersion ?? 1;

  if (typeof manifest !== 'object' || manifest === null) {
    return { accepted: false, error: 'unreadable_response' };
  }
  const rawBody = (manifest as Record<string, unknown>)['body'];
  const rawSignatures = (manifest as Record<string, unknown>)['signatures'];
  if (
    typeof rawBody !== 'object' ||
    rawBody === null ||
    Array.isArray(rawBody) ||
    !Array.isArray(rawSignatures)
  ) {
    return { accepted: false, error: 'unreadable_response' };
  }
  const body = rawBody as Record<string, unknown>;

  const material = utf8(canonical(body));
  const matching = await countValidSignatures(material, rawSignatures, options.allowedKeys);
  if (matching < options.threshold) {
    return { accepted: false, error: 'manifest_bad_signature' };
  }

  const protocolVersion = body['protocol_version'];
  if (!Number.isInteger(protocolVersion) || protocolVersion !== expectedProtocol) {
    return { accepted: false, error: 'unsupported_version' };
  }

  const version = body['version'];
  if (!Number.isInteger(version)) {
    return { accepted: false, error: 'unreadable_response' };
  }
  if ((version as number) < options.knownVersion) {
    return { accepted: false, error: 'manifest_too_old' };
  }

  const validUntil = parseTime(body['valid_until']);
  if (validUntil === null) {
    return { accepted: false, error: 'unreadable_response' };
  }
  const toleranceMs = toleranceFrom(body, clockToleranceS) * 1000;
  if (options.now.getTime() > validUntil + toleranceMs) {
    return { accepted: false, error: 'manifest_expired' };
  }

  // A manifest issued "in the future" by more than the tolerance means the device's
  // clock is wrong, not that the data is bad. A distinct code lets the user be shown
  // the real cause instead of a misleading directory-outage message.
  const issuedAt = parseTime(body['issued_at']);
  if (issuedAt !== null && issuedAt > options.now.getTime() + toleranceMs) {
    return { accepted: false, error: 'clock_unreliable' };
  }

  return { accepted: true, body };
}

function toleranceFrom(body: Record<string, unknown>, fallbackS: number): number {
  const seconds = body['clock_tolerance_s'];
  return Number.isInteger(seconds) ? (seconds as number) : fallbackS;
}

function parseTime(value: unknown): number | null {
  if (typeof value !== 'string') {
    return null;
  }
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}
